import {
    Archive,
    ClientProject,
    clientProjectGet,
    addClientInterface,
    editClientInterface,
    copyDraperyOrder,
    clientGetAll,
    clientGet,
    clientsSearch,
} from "../interfaces/client";

import axiosInstance from "./axios-instance";
import { isMutationSuccess } from "./cache-utils";
import {
    invalidateClientDataCache,
    invalidateOrderDataCache,
    resolveClientId,
    resolveOrderId,
} from "./cache-invalidation";

const getScopedAccountId = (accountId?: number): number => {
    if (typeof window === "undefined") {
        return Number(accountId || 0);
    }
    const ownerAccountId = Number(localStorage.getItem("ownerAccountId") || "0");
    if (ownerAccountId > 0) {
        return ownerAccountId;
    }
    if (Number(accountId) > 0) {
        return Number(accountId);
    }
    return Number(localStorage.getItem("accountId") || "0");
};

const archiveClientProjectItem = async (payload: Archive) => {
    try {
        const response = await axiosInstance.put<Archive>(
            "/client/clientProjectItem/archive",
            {
                ...payload,
            }
        );
        if (isMutationSuccess(response.data)) {
            invalidateOrderDataCache(
                resolveOrderId(payload, response.data),
                resolveClientId(payload, response.data)
            );
        }
        return response.data;
    } catch (error) {
        console.error("Error with archive client project item:", error);
        throw error;
    }
};

const editClientProject = async (payload: ClientProject) => {
    try {
        const response = await axiosInstance.put<ClientProject>(
            "/client/clientProject/edit",
            {
                ...payload,
            }
        );
        if (isMutationSuccess(response.data)) {
            invalidateOrderDataCache(
                resolveOrderId(payload, response.data),
                resolveClientId(payload, response.data)
            );
        }
        return response.data;
    } catch (error) {
        console.error("Error with edit client project:", error);
        throw error;
    }
};

const getClientProjects = async (payload: clientProjectGet) => {
    try {
        const scopedAccountId = getScopedAccountId(payload.accountId);
        const response: any = await axiosInstance.post<clientProjectGet>(
            `/client/clientProject/gets`,
            {
                ...payload,
                accountId: scopedAccountId,
            },
            {
                params: {
                    accountId: scopedAccountId,
                    clientId: payload.clientId,
                    includeArchived: payload.includeArchived,
                },
            }
        );
        return { payload: response.data, error: response.error };
    } catch (error) {
        console.error("Error with get client projects:", error);
        throw error;
    }
};

const addClient = async (payload: addClientInterface) => {
    try {
        const scopedAccountId = getScopedAccountId(payload.accountId);
        const response: any = await axiosInstance.post<addClientInterface>(
            `/client/add`,
            {
                ...payload,
                accountId: scopedAccountId,
            }
        );
        if (isMutationSuccess(response.data)) {
            invalidateClientDataCache(resolveClientId(response.data?.clientId, response.data?.id, payload.client?.id, payload.client));
        }

        return response.data;
    } catch (error) {
        console.error("Error with add client:", error);
        throw error;
    }
};

const editClient = async (payload: editClientInterface) => {
    try {
        const response: any = await axiosInstance.post<editClientInterface>(
            `/client/edit`,
            { ...payload.client }
        );
        if (isMutationSuccess(response.data)) {
            invalidateClientDataCache(resolveClientId(payload.client?.clientId, payload.client?.id, response.data?.clientId, response.data?.id, payload.client, response.data));
        }

        return response.data;
    } catch (error) {
        console.error("Error with edit client:", error);
        throw error;
    }
};

const getAllClients = async (payload: clientGetAll) => {
    try {
        const scopedAccountId = getScopedAccountId(payload.accountId);
        const response: any = await axiosInstance.post<clientGetAll>(
            `/client/get_all`, {
                accountId: scopedAccountId,
                sort: payload.sort,
                includeInactive: payload.includeInactive,
            }
        );

        return response.data;
    } catch (error) {
        console.error("Error with get all clients:", error);
        throw error;
    }
};

const getClientOptions = async (payload: clientGetAll) => {
    try {
        const scopedAccountId = getScopedAccountId(payload.accountId);
        const response: any = await axiosInstance.post<clientGetAll>(
            `/client/get_options`, {
                accountId: scopedAccountId,
                sort: payload.sort,
                includeInactive: payload.includeInactive,
            }
        );

        return response.data;
    } catch (error) {
        console.error("Error with get client options:", error);
        throw error;
    }
};

const getClientApi = async (payload: clientGet) => {
    try {
        const scopedAccountId = payload.accountId ? getScopedAccountId(payload.accountId) : 0;
        const response: any = await axiosInstance.get<clientGet>(
            `/client/get?clientId=${payload.clientId}${scopedAccountId ? `&accountId=${scopedAccountId}` : ""}`
        );

        return response.data;
    } catch (error) {
        console.error("Error with get clients:", error);
        throw error;
    }
};

const searchClients = async (payload: clientsSearch) => {
    try {
        const scopedAccountId = getScopedAccountId(payload.accountId);
        const response: any = await axiosInstance.post<clientsSearch>(
            `/client/search_clients?accountId=${scopedAccountId}&excludeId=${payload.excludeId}&includeInactive=${payload.includeInactive}`,
            { text: payload.text }
        );

        return response.data;
    } catch (error) {
        console.error("Error with search clients:", error);
        throw error;
    }
};

const draperyOrderCopy = async (payload: copyDraperyOrder) => {
    try {
        const scopedAccountId = getScopedAccountId(payload.accountId);
        const response = await axiosInstance.post<copyDraperyOrder>(
            `/client/drapery_order_copy`,
            {
                ...payload,
                accountId: scopedAccountId,
            }
        );
        if (isMutationSuccess(response.data)) {
            invalidateOrderDataCache(undefined, resolveClientId(payload, response.data));
        }

        return response.data;
    } catch (error) {
        console.error("Error with copy drapery order:", error);
        throw error;
    }
};

const deleteClientProject = async (payload: ClientProject) => {
    try {
        const response = await axiosInstance.post(
            "/client/clientProject/delete",
            { ...payload }
        );
        if (isMutationSuccess(response.data)) {
            invalidateOrderDataCache(
                resolveOrderId(payload, response.data),
                resolveClientId(payload, response.data)
            );
        }
        return response.data;
    } catch (error) {
        throw error;
    }
};

const getAllProjects = async (
    accountId: number,
    includeArchived: boolean = false,
    sort: { field: string; direction: "ASC" | "DESC" },
    limit: number,
    pageNumber: number,
    searchTerm: any,
    filters: any
) => {
    try {
        const scopedAccountId = getScopedAccountId(accountId);
        const response = await axiosInstance.post("/client/get-all-projects", {
            accountId: scopedAccountId,
            includeArchived,
            sort,
            limit,
            pageNumber,
            searchTerm,
            filters
        });
        return response.data;
    } catch (error) {
        console.error("Error with get all projects:", error);
        throw error;
    }
};

export {
    archiveClientProjectItem,
    editClientProject,
    draperyOrderCopy,
    getClientProjects,
    searchClients,
    getClientApi,
    editClient,
    getAllClients,
    getClientOptions,
    addClient,
    deleteClientProject,
    getAllProjects,
};
